import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent
} from "react";
import {
  ApiRequestError,
  fetchPriceHistory,
  fetchPriceList,
  savePriceList
} from "./api";
import type {
  PriceHistoryEntry,
  PriceItem,
  PriceListResponse
} from "../shared/price-list";

const PASSWORD_KEY = "business-docs-password";

function formatWon(value: number): string {
  return new Intl.NumberFormat("ko-KR", {
    style: "currency",
    currency: "KRW",
    maximumFractionDigits: 0
  }).format(value);
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("ko-KR", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

function makeItemId(): string {
  return "item_" + crypto.randomUUID().replaceAll("-", "").slice(0, 16);
}

interface LoginProps {
  loading: boolean;
  error: string;
  onLogin: (password: string) => Promise<void>;
}

function Login({ loading, error, onLogin }: LoginProps) {
  const [input, setInput] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onLogin(input);
  }

  return (
    <main className="login-shell">
      <section className="login-card" aria-labelledby="login-title">
        <div className="brand-mark" aria-hidden="true">
          문
        </div>
        <p className="eyebrow">BUSINESS DOCUMENTS</p>
        <h1 id="login-title">사업 문서함</h1>
        <p className="login-copy">
          가격과 문서 기록을 안전하게 관리합니다.
        </p>
        <form onSubmit={submit}>
          <label htmlFor="password">접속 비밀번호</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="비밀번호 입력"
            required
            autoFocus
          />
          {error && <p className="form-error">{error}</p>}
          <button className="primary-button login-button" disabled={loading}>
            {loading ? "확인 중…" : "문서함 열기"}
          </button>
        </form>
      </section>
    </main>
  );
}

interface HistoryPanelProps {
  entries: PriceHistoryEntry[];
  loading: boolean;
  onClose: () => void;
}

function HistoryPanel({ entries, loading, onClose }: HistoryPanelProps) {
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="history-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="history-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sheet-header">
          <div>
            <p className="eyebrow">VERSION HISTORY</p>
            <h2 id="history-title">가격표 변경 기록</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>
        {loading ? (
          <p className="empty-state">기록을 불러오는 중입니다.</p>
        ) : (
          <ol className="history-list">
            {entries.map((entry) => (
              <li key={entry.version}>
                <div className="history-version">v{entry.version}</div>
                <div>
                  <strong>{entry.note || "변경 메모 없음"}</strong>
                  <p>
                    {formatDate(entry.createdAt)} · 상품 {entry.itemCount}개
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

export default function App() {
  const [password, setPassword] = useState(
    () => sessionStorage.getItem(PASSWORD_KEY) ?? ""
  );
  const [authenticated, setAuthenticated] = useState(false);
  const [priceList, setPriceList] = useState<PriceListResponse | null>(null);
  const [draft, setDraft] = useState<PriceItem[]>([]);
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(Boolean(password));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<PriceHistoryEntry[]>([]);

  const total = useMemo(
    () => (priceList?.items ?? []).reduce((sum, item) => sum + item.price, 0),
    [priceList]
  );

  const handleUnauthorized = useCallback(() => {
    sessionStorage.removeItem(PASSWORD_KEY);
    setPassword("");
    setAuthenticated(false);
    setPriceList(null);
  }, []);

  const login = useCallback(async (candidate: string) => {
    setLoading(true);
    setError("");
    try {
      const next = await fetchPriceList(candidate);
      sessionStorage.setItem(PASSWORD_KEY, candidate);
      setPassword(candidate);
      setPriceList(next);
      setAuthenticated(true);
    } catch (requestError) {
      const message =
        requestError instanceof Error
          ? requestError.message
          : "로그인하지 못했습니다.";
      setError(message);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (password && !authenticated) {
      void login(password);
    }
  }, [authenticated, login, password]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  function beginEdit() {
    if (!priceList) return;
    setDraft(priceList.items.map((item) => ({ ...item })));
    setNote("");
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setDraft([]);
    setNote("");
  }

  function updateItem(id: string, patch: Partial<PriceItem>) {
    setDraft((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  }

  function addItem() {
    setDraft((current) => [
      ...current,
      { id: makeItemId(), name: "새 상품", unit: "개", price: 0 }
    ]);
  }

  function removeItem(id: string) {
    if (draft.length === 1) {
      setToast("가격표에는 상품이 하나 이상 필요합니다.");
      return;
    }
    setDraft((current) => current.filter((item) => item.id !== id));
  }

  async function submitPriceList(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const next = await savePriceList(password, {
        items: draft,
        form: { note }
      });
      setPriceList(next);
      setEditing(false);
      setDraft([]);
      setNote("");
      setToast("새 버전으로 저장했습니다.");
    } catch (requestError) {
      if (requestError instanceof ApiRequestError && requestError.status === 401) {
        handleUnauthorized();
        return;
      }
      setError(
        requestError instanceof Error
          ? requestError.message
          : "저장하지 못했습니다."
      );
    } finally {
      setSaving(false);
    }
  }

  async function openHistory() {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const response = await fetchPriceHistory(password);
      setHistory(response.entries);
    } catch (requestError) {
      if (requestError instanceof ApiRequestError && requestError.status === 401) {
        handleUnauthorized();
      } else {
        setToast(
          requestError instanceof Error
            ? requestError.message
            : "기록을 불러오지 못했습니다."
        );
      }
    } finally {
      setHistoryLoading(false);
    }
  }

  function logout() {
    handleUnauthorized();
    setError("");
  }

  if (!authenticated || !priceList) {
    return <Login loading={loading} error={error} onLogin={login} />;
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="wordmark" href="/" aria-label="사업 문서함 홈">
          <span className="wordmark-mark">문</span>
          <span>사업 문서함</span>
        </a>
        <button className="text-button" onClick={logout}>
          나가기
        </button>
      </header>

      <main className="content">
        <section className="hero">
          <div>
            <p className="eyebrow">PRICE BOOK · KRW</p>
            <h1>{priceList.priceBookName}</h1>
            <p className="hero-copy">
              현재 가격을 확인하고, 수정 사항을 새 버전으로 남깁니다.
            </p>
          </div>
          <div className="hero-actions">
            <button className="secondary-button" onClick={openHistory}>
              변경 기록
            </button>
            {!editing && (
              <button className="primary-button" onClick={beginEdit}>
                가격표 수정
              </button>
            )}
          </div>
        </section>

        <section className="status-strip" aria-label="가격표 상태">
          <div>
            <span>현재 버전</span>
            <strong>v{priceList.version}</strong>
          </div>
          <div>
            <span>마지막 저장</span>
            <strong>{formatDate(priceList.createdAt)}</strong>
          </div>
          <div>
            <span>등록 상품</span>
            <strong>{priceList.items.length}개</strong>
          </div>
        </section>

        {editing ? (
          <form className="price-panel edit-panel" onSubmit={submitPriceList}>
            <div className="panel-heading">
              <div>
                <p className="eyebrow">EDIT PRICE BOOK</p>
                <h2>가격표 수정</h2>
              </div>
              <button
                className="secondary-button compact"
                type="button"
                onClick={addItem}
              >
                + 상품 추가
              </button>
            </div>

            <div className="edit-table">
              <div className="edit-row edit-head" aria-hidden="true">
                <span>상품명</span>
                <span>단위</span>
                <span>가격</span>
                <span></span>
              </div>
              {draft.map((item) => (
                <div className="edit-row" key={item.id}>
                  <label>
                    <span>상품명</span>
                    <input
                      value={item.name}
                      maxLength={100}
                      onChange={(event) =>
                        updateItem(item.id, { name: event.target.value })
                      }
                      required
                    />
                  </label>
                  <label>
                    <span>단위</span>
                    <input
                      value={item.unit}
                      maxLength={20}
                      onChange={(event) =>
                        updateItem(item.id, { unit: event.target.value })
                      }
                      required
                    />
                  </label>
                  <label>
                    <span>가격</span>
                    <div className="price-input">
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={1_000_000_000}
                        step={1}
                        value={item.price}
                        onChange={(event) =>
                          updateItem(item.id, {
                            price: Number(event.target.value)
                          })
                        }
                        required
                      />
                      <span>원</span>
                    </div>
                  </label>
                  <button
                    className="remove-button"
                    type="button"
                    onClick={() => removeItem(item.id)}
                    aria-label={item.name + " 삭제"}
                  >
                    삭제
                  </button>
                </div>
              ))}
            </div>

            <label className="note-field">
              <span>변경 메모</span>
              <textarea
                value={note}
                maxLength={300}
                rows={3}
                onChange={(event) => setNote(event.target.value)}
                placeholder="예: 7월 공급가 반영"
              />
            </label>
            <p className="version-notice">
              저장하면 기존 가격표를 수정하지 않고 v{priceList.version + 1}로
              기록합니다.
            </p>
            {error && <p className="form-error">{error}</p>}
            <div className="form-actions">
              <button
                className="secondary-button"
                type="button"
                onClick={cancelEdit}
                disabled={saving}
              >
                취소
              </button>
              <button className="primary-button" disabled={saving}>
                {saving ? "저장 중…" : "새 버전으로 저장"}
              </button>
            </div>
          </form>
        ) : (
          <section className="price-panel">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">CURRENT PRICES</p>
                <h2>상품 가격</h2>
              </div>
              <p className="item-total">가격 합계 {formatWon(total)}</p>
            </div>
            <div className="price-list">
              {priceList.items.map((item, index) => (
                <article className="price-row" key={item.id}>
                  <span className="row-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="product-name">
                    <strong>{item.name}</strong>
                    <span>1{item.unit} 기준</span>
                  </div>
                  <strong className="product-price">
                    {formatWon(item.price)}
                  </strong>
                </article>
              ))}
            </div>
          </section>
        )}

        <footer className="footer">
          가격을 저장할 때마다 당시 입력 내용이 변경 이력에 보존됩니다.
        </footer>
      </main>

      {toast && <div className="toast">{toast}</div>}
      {historyOpen && (
        <HistoryPanel
          entries={history}
          loading={historyLoading}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </div>
  );
}
